import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = (this.#resetTimeouts || []).indexOf(timeoutId);
      if (index > -1 && this.#resetTimeouts) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    if (!this.#resetTimeouts) this.#resetTimeouts = [];
    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #addonVariantCache = new Map();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];



  // Event listener wrappers to satisfy DOM typings (accept Event and cast internally)
  /** @param {Event} event */
  #boundOnVariantUpdate = (event) => {
    void this.#onVariantUpdate(/** @type {any} */(event));
  };

  /** @param {Event} event */
  #boundOnVariantSelected = (event) => {
    void this.#onVariantSelected(/** @type {any} */(event));
  };

  /** @param {Event} event */
  #boundOnCartUpdate = (event) => {
    void this.#onCartUpdate(/** @type {any} */(event));
  };


  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    // Use Event-compatible wrappers and cast event names to string to satisfy DOM typings
    target?.addEventListener(/** @type {string} */(ThemeEvents.variantUpdate), this.#boundOnVariantUpdate, {
      signal,
    });
    target?.addEventListener(/** @type {string} */(ThemeEvents.variantSelected), this.#boundOnVariantSelected, {
      signal,
    });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(/** @type {string} */(ThemeEvents.cartUpdate), this.#boundOnCartUpdate, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    console.log('[warranty] FormData before overrides:', Array.from(formData.entries()));
    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const section = this.closest('.shopify-section') || this.closest('dialog');
    const warrantyComponent = section?.querySelector('extended-warranty-component');
    const warrantyRadio = /** @type {HTMLInputElement | null | undefined} */ (warrantyComponent?.querySelector(
      'input[type="radio"][name="properties[Garantia Extendida]"]:checked'
    ));
    const protectedPurchaseCheckbox = /** @type {HTMLInputElement | null | undefined} */ (warrantyComponent?.querySelector(
      'input[type="checkbox"][name="properties[Compra Protegida]"]:checked'
    ));
    const protectedPurchaseName = protectedPurchaseCheckbox?.value || '';
    const protectedPurchaseType = protectedPurchaseCheckbox?.dataset.protectedPurchaseType || 'fixed';
    const protectedPurchaseImageUrl = protectedPurchaseCheckbox?.dataset.protectedPurchaseImageUrl || '';
    const protectedPurchaseConditionsRaw = /** @type {HTMLElement | null | undefined} */ (warrantyComponent)
      ?.dataset?.protectedPurchaseConditions || '[]';
    const protectedPurchaseConditions = this.#parseProtectedPurchaseConditions(protectedPurchaseConditionsRaw);
    const hasProtectedPurchase = Boolean(protectedPurchaseName) && protectedPurchaseConditions.length > 0;
    const productTitle = this.#getCurrentProductTitle();
    const productQuantityForAddOn = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    // Si no hay radio de garantía seleccionado con valor, seguir flujo normal
    if (!warrantyRadio || !warrantyRadio.value) {
      if (hasProtectedPurchase) {
        this.#appendProtectedPurchasePropertiesToFormData(formData, {
          productTitle,
          productQuantity: productQuantityForAddOn,
          protectedPurchaseName,
          protectedPurchaseConditions,
          protectedPurchaseImageUrl,
        });
        // Suprimir auto-open del cart drawer y mostrar modal de carga
        const cartDrawer = document.querySelector('cart-drawer-component');
        const hadAutoOpen = Boolean(cartDrawer?.hasAttribute('auto-open'));
        if (hadAutoOpen) {
          cartDrawer?.removeAttribute('auto-open');
        }
        cartDrawer?.setAttribute('data-protected-purchase-processing', '');
        this.#showLoadingModal('Procesando Compra Protegida...');
        this.#awaitProtectedPurchaseSync(cartDrawer, hadAutoOpen);
        this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event)
          ?.catch(() => {
            this.#finalizeProtectedPurchaseModal(cartDrawer, hadAutoOpen);
          });
      } else {
        this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event);
      }
      return;
    }

    const warrantyName = warrantyRadio.dataset.warrantyDuration || warrantyRadio.value;
    const warrantyCost = warrantyRadio.dataset.warrantyCost;
    const warrantyType = warrantyRadio.dataset.warrantyType;
    const warrantyImageUrl = warrantyRadio.dataset.warrantyImageUrl || '';

    // Si no tiene costo o es 0, seguir flujo normal sin garantía
    if (!warrantyCost || parseFloat(warrantyCost) <= 0) {
      if (hasProtectedPurchase) {
        this.#appendProtectedPurchasePropertiesToFormData(formData, {
          productTitle,
          productQuantity: productQuantityForAddOn,
          protectedPurchaseName,
          protectedPurchaseConditions,
          protectedPurchaseImageUrl,
        });
        // Suprimir auto-open del cart drawer y mostrar modal de carga
        const cartDrawer = document.querySelector('cart-drawer-component');
        const hadAutoOpen = Boolean(cartDrawer?.hasAttribute('auto-open'));
        if (hadAutoOpen) {
          cartDrawer?.removeAttribute('auto-open');
        }
        cartDrawer?.setAttribute('data-protected-purchase-processing', '');
        this.#showLoadingModal('Procesando Compra Protegida...');
        this.#awaitProtectedPurchaseSync(cartDrawer, hadAutoOpen);
        this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event)
          ?.catch(() => {
            this.#finalizeProtectedPurchaseModal(cartDrawer, hadAutoOpen);
          });
      } else {
        this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event);
      }
      return;
    }

    // Cantidad del producto y costo unitario de garantía
    const productQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const warrantyUnitCost = parseFloat(warrantyCost);

    // Suprimir auto-open del cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    const hadAutoOpen = Boolean(cartDrawer?.hasAttribute('auto-open'));
    if (hadAutoOpen) {
      cartDrawer?.removeAttribute('auto-open');
    }

    // Mostrar modal de carga mientras se obtiene el variant ID de la garantía
    this.#showLoadingModal();

    // Obtener el variant ID del producto principal
    const variantId = formData.get('id')?.toString();

    // Obtener warranty variantId PRIMERO
    // Enviamos el costo unitario; la cantidad se maneja con warrantyItem.quantity
    this.#ensureAddonVariantId({
      productId: variantId || this.dataset.productId || '',
      warrantyName,
      warrantyCost: String(warrantyUnitCost),
      warrantyType: warrantyType || 'fixed',
      warrantyImageUrl,
      customerId: this.#getCustomerId(),
      shopId: window.Shopify?.shop || '',
    })
      .then((addonVariantId) => {
        if (addonVariantId) {
          const productVariantId = variantId || this.refs.variantId?.value || '';
          const bundleId = 'bundle_' + Date.now() + '_' + productVariantId;


          // 1) Agregar producto PRIMERO
          const productItem = {
            id: Number(productVariantId),
            quantity: productQuantity,
            properties: /** @type {Record<string, string>} */ ({ '_Bundle_ID': bundleId }),
          };

          if (hasProtectedPurchase) {
            productItem.properties['Compra Protegida'] = protectedPurchaseName;
            productItem.properties._Protected_Purchase_Eligible = 'true';
            productItem.properties._Protected_Purchase_Title = protectedPurchaseName;
            productItem.properties._Protected_Purchase_Conditions = JSON.stringify(protectedPurchaseConditions);
            productItem.properties._Protected_Purchase_Product = `${productTitle} x${productQuantityForAddOn}`;
            if (protectedPurchaseImageUrl) {
              productItem.properties._Protected_Purchase_Image_URL = protectedPurchaseImageUrl;
            }
          }

          // Leer inputs ocultos del componente de garantía
          const hiddenCost = /** @type {HTMLInputElement | null} */ (
            warrantyComponent?.querySelector('[ref="hiddenCostInput"]')
          )?.value || '';
          const hiddenType = /** @type {HTMLInputElement | null} */ (
            warrantyComponent?.querySelector('[ref="hiddenTypeInput"]')
          )?.value || '';

          // Usar el costo ya computado por extended-warranty.js (maneja percentage vs fixed)
          const resolvedWarrantyCost = hiddenCost || String(warrantyUnitCost);

          // Obtener nombre visible del producto para mostrar en cart
          const productTitleForCart =
            document.querySelector('h1')?.textContent?.trim() ||
            /** @type {HTMLElement | null} */ (document.querySelector('[data-product-title]'))?.dataset?.productTitle ||
            productVariantId;

          // 2) Luego garantía al FINAL del carrito — misma cantidad que el producto
          const warrantyItem = {
            id: Number(addonVariantId),
            quantity: productQuantity,
            properties: /** @type {Record<string, string>} */ ({
              '_Bundle_ID': bundleId,
              '_Product_type': "Warranty",
              '_Addon_Name': warrantyName,
              '_Addon_Cost': resolvedWarrantyCost,
              '_Addon_Type': warrantyType || hiddenType || 'fixed',
              'Para ': productTitleForCart,
            }),
          };

          console.log('[warranty] Item properties antes de limpiar:', JSON.stringify(warrantyItem.properties));

          // Limpiar properties vacías
          for (const key of Object.keys(warrantyItem.properties)) {
            if (!warrantyItem.properties[key]) delete warrantyItem.properties[key];
          }

          console.log('[warranty] Item properties después de limpiar:', JSON.stringify(warrantyItem.properties));

          return this.#addBundleItemsSequentially(productItem, warrantyItem, event);
        } else {
          // Fallback: agregar solo el producto principal
          if (hasProtectedPurchase) {
            this.#appendProtectedPurchasePropertiesToFormData(formData, {
              productTitle,
              productQuantity: productQuantityForAddOn,
              protectedPurchaseName,
              protectedPurchaseConditions,
              protectedPurchaseImageUrl,
            });
            return this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event);
          }

          this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event);
        }
      })
      .then(() => {
        this.#hideLoadingModal();
        this.#restoreCartDrawer(cartDrawer, hadAutoOpen);
      })
      .catch((err) => {
        console.error('[warranty] Bundle error, fallback a producto individual:', err);
        if (hasProtectedPurchase) {
          this.#appendProtectedPurchasePropertiesToFormData(formData, {
            productTitle,
            productQuantity: productQuantityForAddOn,
            protectedPurchaseName,
            protectedPurchaseConditions,
            protectedPurchaseImageUrl,
          });
          this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event);
        } else {
          this.#submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event);
        }
        this.#hideLoadingModal();
        this.#restoreCartDrawer(cartDrawer, hadAutoOpen);
      });
    return;
  }

  /**
   * Obtiene o genera un customerId persistente.
   * - Si el cliente está logueado (Shopify.customer.id), lo guarda en localStorage y lo retorna.
   * - Si es anónimo, busca un ID previo en localStorage o genera uno nuevo (UUID v4).
   * @returns {string}
   */
  #getCustomerId() {
    const STORAGE_KEY = 'kliquea_customer_id';
    const shopify = /** @type {any} */ (window.Shopify);
    const loggedInId = shopify?.customer?.id;

    // Cliente logueado: guardar y retornar su ID real
    if (loggedInId) {
      localStorage.setItem(STORAGE_KEY, String(loggedInId));
      return String(loggedInId);
    }

    // Anónimo: recuperar ID previo o generar uno nuevo
    let anonId = localStorage.getItem(STORAGE_KEY);
    if (!anonId) {
      anonId = 'a_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
      localStorage.setItem(STORAGE_KEY, anonId);
    }
    return anonId;
  }

  /**
   * Llama al endpoint para crear/obtener dinámicamente una variante de garantía
   * y devuelve el addonVariantId.
   * @param {{productId: string, warrantyName: string, warrantyCost: string, warrantyType: string, warrantyImageUrl: string, customerId: string, shopId: string}} params
   * @returns {Promise<string | null>}
   */
  async #ensureAddonVariantId({ productId, warrantyName, warrantyCost, warrantyType, warrantyImageUrl, customerId, shopId }) {
    const cacheKey = `${productId}|${warrantyName}|${warrantyCost}|${warrantyType}`;
    if (this.#addonVariantCache.has(cacheKey)) {
      return this.#addonVariantCache.get(cacheKey);
    }
    try {
      const response = await fetch('https://ldcok5ribe.execute-api.us-east-1.amazonaws.com/apps/warranty/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          productId,
          warrantyName,
          warrantyCost: parseFloat(warrantyCost),
          warrantyType,
          warrantyImageUrl,
          customerId,
          shopId
        }),
      });
      console.log('[warranty] response:', response);
      if (!response.ok) {
        console.error('[warranty] Failed to create warranty variant:', response.status);
        return null;
      }

      const data = await response.json();
      const variantId = data?.data?.variantId || data?.variantId || data?.variant_id || data?.id;
      const variantPrice = data?.data?.price || data?.price || data?.variantPrice;
      const expectedCost = parseFloat(warrantyCost);

      if (variantId) {
        console.log('[warranty] Variant created/found:', {
          variantId,
          warrantyName,
          expectedCost,
          variantPriceFromLambda: variantPrice,
          priceMatch: variantPrice != null ? Math.abs(variantPrice - expectedCost) < 0.01 : 'unknown (no price in response)',
        });
        const id = String(variantId);
        this.#addonVariantCache.set(cacheKey, id);
        return id;
      }

      console.error('[warranty] No variant ID in response:', data);
      return null;
    } catch (error) {
      console.error('[warranty] Error creating warranty variant:', error);
      return null;
    }
  }

  /**
   * Flujo normal de add to cart (sin garantía).
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   * @param {NodeListOf<AddToCartComponent>} allAddToCartContainers
   * @param {HTMLElement | undefined} addToCartTextError
   * @param {Event} [event]
   */
  #submitSingleAddToCart(form, formData, allAddToCartContainers, addToCartTextError, event) {
    const fetchCfg = fetchConfig('javascript', { body: formData });

    return fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // La sincronización consolidada de Compra Protegida se maneja desde cart-items.
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @returns {string} */
  #getCurrentProductTitle() {
    const scope = this.closest('.shopify-section, dialog') || document;
    return (
      scope.querySelector('h1')?.textContent?.trim()
      || /** @type {HTMLElement | null} */ (scope.querySelector('[data-product-title]'))?.dataset?.productTitle
      || `Producto ${this.dataset.productId || ''}`
    );
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionIds);
  }

  /**
   * @param {string} rawConditions
   * @returns {Array<{min_price: number, max_price: number, value: number}>}
   */
  #parseProtectedPurchaseConditions(rawConditions) {
    if (!rawConditions) return [];
    try {
      const parsed = JSON.parse(rawConditions);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((/** @type {any} */ item) => ({
          min_price: Number(item?.min_price || 0),
          max_price: Number(item?.max_price || 0),
          value: Number(item?.value || 0),
        }))
        .filter((/** @type {{min_price:number,max_price:number,value:number}} */ item) => !Number.isNaN(item.min_price) && !Number.isNaN(item.max_price) && !Number.isNaN(item.value))
        .sort((/** @type {{min_price:number}} */ a, /** @type {{min_price:number}} */ b) => a.min_price - b.min_price);
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {FormData} formData
   * @param {{
   *   productTitle: string,
   *   productQuantity: number,
   *   protectedPurchaseName: string,
   *   protectedPurchaseConditions: Array<{min_price: number, max_price: number, value: number}>,
   *   protectedPurchaseImageUrl?: string,
   * }} params
   */
  #appendProtectedPurchasePropertiesToFormData(formData, {
    productTitle,
    productQuantity,
    protectedPurchaseName,
    protectedPurchaseConditions,
    protectedPurchaseImageUrl,
  }) {
    formData.set('properties[Compra Protegida]', protectedPurchaseName);
    formData.set('properties[_Protected_Purchase_Eligible]', 'true');
    formData.set('properties[_Protected_Purchase_Title]', protectedPurchaseName);
    formData.set('properties[_Protected_Purchase_Conditions]', JSON.stringify(protectedPurchaseConditions));
    formData.set('properties[_Protected_Purchase_Product]', `${productTitle} x${productQuantity}`);
    if (protectedPurchaseImageUrl) {
      formData.set('properties[_Protected_Purchase_Image_URL]', protectedPurchaseImageUrl);
    }
  }

  /**
   * @param {{
   *   defaultProtectedPurchaseName?: string,
   *   protectedPurchaseType?: string,
   *   protectedPurchaseImageUrl?: string,
   * }} [config]
   */
  async #syncProtectedPurchaseFromCart(config = {}) {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();

      const items = /** @type {any[]} */ (Array.isArray(cart?.items) ? cart.items : []);
      const protectedIndex = items.findIndex((/** @type {any} */ item) => item?.properties?._Product_type === 'Protected Purchase');
      const protectedItem = protectedIndex >= 0 ? items[protectedIndex] : null;

      const eligibleItems = items.filter((/** @type {any} */ item) => {
        const props = item?.properties || {};
        return props._Protected_Purchase_Eligible === 'true' && props._Product_type !== 'Protected Purchase';
      });

      if (!eligibleItems.length) {
        if (protectedItem) {
          await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              line: protectedIndex + 1,
              quantity: 0,
              sections: this.#getCartSectionIds().join(','),
              sections_url: window.location.pathname,
            }),
          }));
        }
        return;
      }

      const conditionsRaw = eligibleItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Conditions)
        ?.properties?._Protected_Purchase_Conditions;
      const conditions = this.#parseProtectedPurchaseConditions(conditionsRaw || '[]');
      if (!conditions.length) return;

      const subtotal = eligibleItems.reduce((/** @type {number} */ sum, /** @type {any} */ item) => {
        const linePriceCents = Number(item?.final_line_price || item?.line_price || 0);
        return sum + (Number.isNaN(linePriceCents) ? 0 : linePriceCents / 100);
      }, 0);

      let selectedCondition = null;
      for (const condition of conditions) {
        if (subtotal >= condition.min_price && subtotal < condition.max_price) {
          selectedCondition = condition;
          break;
        }
      }

      if (!selectedCondition || !(selectedCondition.value > 0)) {
        if (protectedItem) {
          await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
            body: JSON.stringify({
              line: protectedIndex + 1,
              quantity: 0,
              sections: this.#getCartSectionIds().join(','),
              sections_url: window.location.pathname,
            }),
          }));
        }
        return;
      }

      const addonName =
        eligibleItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Title)?.properties?._Protected_Purchase_Title
        || protectedItem?.properties?._Addon_Name
        || config.defaultProtectedPurchaseName
        || 'Compra Protegida';

      const resolvedProtectedPurchaseImageUrl =
        config.protectedPurchaseImageUrl
        || eligibleItems.find((/** @type {any} */ item) => item?.properties?._Protected_Purchase_Image_URL)?.properties?._Protected_Purchase_Image_URL
        || '';

      const addonVariantId = await this.#ensureAddonVariantId({
        productId: String(eligibleItems[0]?.variant_id || this.dataset.productId || ''),
        warrantyName: addonName,
        warrantyCost: String(selectedCondition.value),
        warrantyType: config.protectedPurchaseType || protectedItem?.properties?._Addon_Type || 'fixed',
        warrantyImageUrl: resolvedProtectedPurchaseImageUrl,
        customerId: this.#getCustomerId(),
        shopId: window.Shopify?.shop || '',
      });

      if (!addonVariantId) return;

      const articleProperties = /** @type {Record<string, string>} */ ({});
      eligibleItems.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
        const title = item?.product_title || item?.title || `Producto ${item?.id || ''}`;
        const variantTitle = item?.variant_title || '';
        const qty = Number(item?.quantity || 1);
        const label = variantTitle ? `${title} - ${variantTitle} x${qty}` : `${title} x${qty}`;
        articleProperties[`Articulo ${index + 1}`] = label;
      });

      const protectedProperties = {
        _Product_type: 'Protected Purchase',
        _Addon_Name: addonName,
        _Addon_Type: config.protectedPurchaseType || protectedItem?.properties?._Addon_Type || 'fixed',
        _Addon_Cost_Total: Number(selectedCondition.value).toFixed(2),
        _Protected_Purchase_Subtotal: subtotal.toFixed(2),
        ...articleProperties,
      };

      const sections = this.#getCartSectionIds().join(',');

      if (protectedItem) {
        await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
          body: JSON.stringify({
            line: protectedIndex + 1,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        }));
      }

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: Number(addonVariantId),
            quantity: 1,
            properties: protectedProperties,
          }],
          sections,
        }),
      });

      const addData = await addResponse.json();
      if (addData?.status) {
        console.error('[protected-purchase] Error syncing protected purchase item:', addData);
        return;
      }

    } catch (error) {
      console.error('[protected-purchase] sync error:', error);
    }
  }

  /** @param {Array<{variantId: string, quantity: number, properties?: Record<string, string>}>} items @param {Event} [event] */
  #processBatchAddToCart(items, event) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => {
        const line = {
          id: Number(item.variantId),
          quantity: item.quantity,
        };
        if (item.properties && Object.keys(item.properties).length > 0) {
          line.properties = item.properties;
        }
        return line;
      }),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Agrega producto y garantía en dos llamadas separadas para que
   * la garantía quede SIEMPRE al final del carrito.
   * @param {{id: number, quantity: number, properties?: Record<string, string>}} productItem
   * @param {{id: number, quantity: number, properties?: Record<string, string>}} warrantyItem
   * @param {Event} [event]
   */
  #addBundleItemsSequentially(productItem, warrantyItem, event) {
    const { addToCartTextError } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const body = {
      items: [productItem],
      sections: sectionIds.join(','),
    };

    // 1) Agregar producto
    // Retornamos la promesa para que el caller pueda esperar a que termine
    return fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((productResponse) => {
        if (productResponse.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, productResponse.message, productResponse.description, productResponse.errors)
          );
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = productResponse.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(productResponse.message));
            }
            this.#setLiveRegionText(productResponse.message);
            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }
          return;
        }

        // 2) Producto agregado OK → agregar garantía al final
        const warrantyBody = {
          items: [warrantyItem],
          sections: sectionIds.join(','),
        };

        return fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(warrantyBody),
        }).then((warrantyResponse) => warrantyResponse.json());
      })
      .then((warrantyResponse) => {
        if (!warrantyResponse) return; // hubo error en producto

        if (warrantyResponse.status) {
          // Error al agregar garantía – el producto ya se agregó
          console.error('[warranty] Error adding warranty item:', warrantyResponse);
          this.#fetchAndUpdateCartQuantity().then((cart) => {
            this.dispatchEvent(
              new CartAddEvent(cart ?? undefined, this.id, {
                source: 'product-form-component',
                itemCount: productItem.quantity,
                productId: this.dataset.productId,
                sections: warrantyResponse.sections,
              })
            );
          });
          return;
        }

        // Ambos items agregados correctamente
        const totalQuantity = productItem.quantity + (warrantyItem.quantity || 1);

        this.#fetchAndUpdateCartQuantity().then((cart) => {
          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, this.id, {
              source: 'product-form-component',
              itemCount: totalQuantity,
              productId: this.dataset.productId,
              sections: warrantyResponse.sections,
            })
          );
        });

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }
      })
      .catch((error) => {
        console.error('[warranty] Sequential add error:', error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Muestra un modal de carga mientras se procesa la garantía extendida.
   * Se cierra automáticamente cuando #hideLoadingModal es llamado.
   */
  #showLoadingModal(message) {
    this.#hideLoadingModal();

    const overlay = document.createElement('div');
    overlay.className = 'warranty-loading-overlay';
    overlay.setAttribute('popover', 'manual');
    overlay.setAttribute('aria-label', message || 'Procesando');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="warranty-loading-modal">
        <div class="warranty-loading-spinner"></div>
        <p class="warranty-loading-text">${message || Theme.translations?.warrantyProcessing || 'Procesando...'}</p>
      </div>
    `;

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // Forzar top-layer con popover para que quede encima de quick-add-modal
    // (los <dialog> nativos usan top-layer y ningún z-index puede superarlos)
    if (typeof overlay.showPopover === 'function') {
      overlay.showPopover();
    }
  }

  /**
   * Oculta el modal de carga de garantía.
   */
  #hideLoadingModal() {
    const overlay = document.querySelector('.warranty-loading-overlay');
    if (overlay) {
      if (typeof overlay.hidePopover === 'function') {
        overlay.hidePopover();
      }
      overlay.remove();
      document.body.style.overflow = '';
    }
  }

  /**
   * Restaura el atributo auto-open del cart drawer y fuerza un refresh
   * para que muestre los items agregados.
   * @param {Element | null} cartDrawer
   * @param {boolean} hadAutoOpen
   */
  #restoreCartDrawer(cartDrawer, hadAutoOpen) {
    if (hadAutoOpen && cartDrawer) {
      cartDrawer.setAttribute('auto-open', '');
      // Abrir el drawer ya que el modal de loading se cerró
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }
    // Forzar refresh del drawer para que muestre ambos items
    if (cartDrawer) {
      cartDrawer.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
    }
  }

  /**
   * Resuelve una vez que el sync de Compra Protegida termine o tras timeout.
   * @param {Element | null} cartDrawer
   * @param {boolean} hadAutoOpen
   */
  #awaitProtectedPurchaseSync(cartDrawer, hadAutoOpen) {
    let resolved = false;
    const finalize = () => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('protected-purchase:sync-complete', finalize);
      clearTimeout(timeout);
      this.#finalizeProtectedPurchaseModal(cartDrawer, hadAutoOpen);
    };
    document.addEventListener('protected-purchase:sync-complete', finalize);
    const timeout = setTimeout(finalize, 10000);
  }

  /**
   * Cierra el modal de loading de Compra Protegida y restaura el cart drawer.
   * @param {Element | null} cartDrawer
   * @param {boolean} hadAutoOpen
   */
  #finalizeProtectedPurchaseModal(cartDrawer, hadAutoOpen) {
    this.#hideLoadingModal();
    if (cartDrawer) {
      cartDrawer.removeAttribute('data-protected-purchase-processing');
    }
    this.#restoreCartDrawer(cartDrawer, hadAutoOpen);
  }
  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */(newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
